/**
 * Runtime primary application (02-design-system.md §2.3, 01-architecture.md §6).
 *
 * Validated hex in → `--primary` + derived ramp out. With native `color-mix()`
 * the CSS in tokens.css derives the ramp from `--primary` alone; without it
 * (older/embedded WebViews) this module computes an sRGB approximation and
 * writes every ramp variable inline, so theming can never make controls
 * disappear. Colors that cannot produce a compliant foreground pair are
 * rejected: the default brand stays applied and a sanitized diagnostic (color
 * + reason only, never user/org data) is logged.
 */
import {
  DEFAULT_PRIMARY,
  INK_HEX,
  MIN_TEXT_CONTRAST,
  contrastRatio,
  WHITE_HEX,
  hexToRgb,
  mixHex,
  normalizeHex,
  pickPrimaryForeground,
  textSafeShade,
} from './contrast';

/** Semantic surfaces the primary must remain distinguishable against. */
const LIGHT_CARD = '#FFFFFF';
/*
  THESE MIRROR tokens.css AND HAD STOPPED.

  The dark palette was neutralised — #0b1017 -> #101010, #151c28 -> #1c1c1c,
  #1d2634 -> #262626 (tokens.css) — and these three were not moved with it. So
  every per-org contrast gate below (`textSafeShade`, `pickPrimaryForeground`,
  `MIN_FILL_DISTINCTION`) was measuring a tenant's colour against surfaces the
  theme had already deleted, and passing or failing it for a UI that no longer
  ships.
*/
const DARK_CARD = '#1C1C1C';
/** Hardest light surface for primary-colored text (darker than the card). */
const LIGHT_TEXT_SURFACE = '#F4F4F5';
/** Hardest dark surface for primary-colored text (lighter than the card). */
const DARK_TEXT_SURFACE = '#101010';

/**
 * Minimum fill-vs-card contrast before a primary is considered usable in a
 * theme. The spec's 3:1 non-text target cannot gate this check — the locked
 * default brand itself measures 2.37:1 on a white card — so the floor sits
 * just under the default brand while still rejecting near-invisible fills
 * (#FFFFFF on light ≈ 1.0, #000000 on dark ≈ 1.2). Real 3:1 boundary work is
 * the V1-09 audit's scope.
 */
export const MIN_FILL_DISTINCTION = 1.9;

const RAMP_PROPERTIES = [
  '--primary',
  '--primary-foreground',
  /* The fill pair: the brand tone itself, with white on it. Cleared with the
     rest of the ramp so a rejected brand colour leaves no stale fill behind. */
  '--primary-strong',
  '--primary-strong-foreground',
  '--primary-strong-hover',
  '--primary-hover',
  '--primary-active',
  '--primary-tint',
  '--primary-ring',
  '--primary-glow',
  '--primary-text-l',
  '--primary-text-d',
] as const;

export type PrimaryRejectionReason =
  'invalid-hex' | 'no-compliant-foreground' | 'indistinct-in-light' | 'indistinct-in-dark';

export type PrimaryEvaluation =
  { ok: true; hex: string; foreground: string } | { ok: false; reason: PrimaryRejectionReason };

/** Pure validation: can this org color power the primary ramp? */
export function evaluatePrimary(input: unknown): PrimaryEvaluation {
  const hex = normalizeHex(input);
  if (!hex) return { ok: false, reason: 'invalid-hex' };
  const foreground = pickPrimaryForeground(hex);
  if (!foreground) return { ok: false, reason: 'no-compliant-foreground' };
  if (contrastRatio(hex, LIGHT_CARD) < MIN_FILL_DISTINCTION)
    return { ok: false, reason: 'indistinct-in-light' };
  if (contrastRatio(hex, DARK_CARD) < MIN_FILL_DISTINCTION)
    return { ok: false, reason: 'indistinct-in-dark' };
  return { ok: true, hex, foreground };
}

/** Default detection; injectable for tests and non-browser environments. */
export function detectColorMixSupport(): boolean {
  try {
    return (
      typeof CSS !== 'undefined' &&
      typeof CSS.supports === 'function' &&
      CSS.supports('color', 'color-mix(in oklch, red, white 10%)')
    );
  } catch {
    return false;
  }
}

function alphaColor(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

export interface AppliedPrimary {
  /** Effective primary (requested color, or the default brand on fallback). */
  hex: string;
  foreground: string;
  fallback: { requested: string; reason: PrimaryRejectionReason } | null;
}

export interface ApplyPrimaryOptions {
  documentElement?: HTMLElement | null;
  supportsColorMix?: () => boolean;
  /** Sanitized diagnostic sink; defaults to console.warn. */
  warn?: (message: string) => void;
}

function clearRamp(root: HTMLElement): void {
  for (const property of RAMP_PROPERTIES) root.style.removeProperty(property);
}

/**
 * Applies (or clears, with `null`) the organization primary on the document.
 * Always leaves the document in a compliant state.
 */
export function applyPrimary(input: unknown, options: ApplyPrimaryOptions = {}): AppliedPrimary {
  const root =
    options.documentElement ?? (typeof document !== 'undefined' ? document.documentElement : null);
  const warn = options.warn ?? ((message: string) => console.warn(message));

  const defaults: AppliedPrimary = {
    hex: DEFAULT_PRIMARY,
    foreground: INK_HEX,
    fallback: null,
  };

  if (input === null || input === undefined || input === '') {
    if (root) clearRamp(root);
    return defaults;
  }

  const evaluation = evaluatePrimary(input);
  if (!evaluation.ok) {
    if (root) clearRamp(root);
    const requested = normalizeHex(input) ?? '(invalid value)';
    warn(
      `[sg-theme] organization primary ${requested} rejected (${evaluation.reason}); ` +
        `using default brand ${DEFAULT_PRIMARY}.`,
    );
    return { ...defaults, fallback: { requested, reason: evaluation.reason } };
  }

  if (!root) return { hex: evaluation.hex, foreground: evaluation.foreground, fallback: null };

  const { hex, foreground } = evaluation;
  root.style.setProperty('--primary', hex);
  root.style.setProperty('--primary-foreground', foreground);
  /*
    THE FILL IS THE BRAND TONE, WITH WHITE ON IT (owner, 2026-08-24: "color dark
    q kara primary color hi use karo bss text white karna tha").

    This pair briefly derived a darker shade so white could clear 4.5:1. The
    owner rejected that: the brand tone is the brand tone, and the fill must be
    it exactly. So --primary-strong IS --primary now, and the foreground is
    white by instruction rather than by measurement.

    STATED PLAINLY BECAUSE IT IS A KNOWN TRADE, NOT AN OVERSIGHT: white on the
    stock brand green measures 2.62:1, under the 4.5:1 that WCAG AA asks of text
    and under the 3:1 it asks of large text. `foreground` above still holds the
    compliant ink this file computes, and every non-filled surface still uses
    it. If the bar is ever raised back, the fix is one of two lines — put
    `foreground` back here, or darken the tenant's brand colour in Themes, after
    which white passes on its own.

    Hover keeps its 12% step toward black: that is depth on press, not contrast.
  */
  root.style.setProperty('--primary-strong', hex);
  root.style.setProperty('--primary-strong-foreground', WHITE_HEX);
  root.style.setProperty('--primary-strong-hover', mixHex(hex, '#000000', 0.12));
  root.style.setProperty(
    '--primary-text-l',
    textSafeShade(hex, LIGHT_TEXT_SURFACE, MIN_TEXT_CONTRAST),
  );
  root.style.setProperty(
    '--primary-text-d',
    textSafeShade(hex, DARK_TEXT_SURFACE, MIN_TEXT_CONTRAST),
  );

  const supportsColorMix = options.supportsColorMix ?? detectColorMixSupport;
  if (!supportsColorMix()) {
    // Static sRGB ramp fallback (spec §2.3 feature detection).
    root.style.setProperty('--primary-hover', mixHex(hex, '#000000', 0.14));
    root.style.setProperty('--primary-active', mixHex(hex, '#000000', 0.24));
    root.style.setProperty('--primary-tint', alphaColor(hex, 0.1));
    root.style.setProperty('--primary-ring', alphaColor(hex, 0.35));
    root.style.setProperty('--primary-glow', alphaColor(hex, 0.4));
  } else {
    for (const property of [
      '--primary-hover',
      '--primary-active',
      '--primary-tint',
      '--primary-ring',
      '--primary-glow',
    ] as const) {
      root.style.removeProperty(property);
    }
  }

  return { hex, foreground, fallback: null };
}
